import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebar } from '@/context/SidebarContext';
import { Plus, Filter, Search, ArrowLeft, Mail, LayoutGrid, CheckSquare, MessageSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface ActionButton {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  href?: string;
}

interface MobileActionBarConfig {
  leftAction?: ActionButton;
  leftSecondary?: ActionButton;
  rightSecondary?: ActionButton;
  rightAction?: ActionButton;
  centerAction: {
    icon: LucideIcon;
    label: string;
    href: string;
  };
}

const ActionSlot = ({ action, size = 'md' }: { action?: ActionButton; size?: 'sm' | 'md' }) => {
  if (!action) return <div className={size === 'sm' ? 'w-9' : 'w-11'} />;

  const sizeClass = size === 'sm' ? 'h-9 w-9' : 'h-11 w-11';
  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const className = `group relative flex items-center justify-center ${sizeClass} rounded-full bg-muted/80 hover:bg-muted transition-all duration-300 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg`;

  const content = (
    <>
      <action.icon className={`${iconSize} text-foreground transition-transform group-hover:scale-110 duration-300`} />
      <div className="absolute inset-0 rounded-full border-2 border-primary/20 scale-100 opacity-0 group-hover:scale-110 group-hover:opacity-100 transition-all duration-300" />
    </>
  );

  if (action.href) {
    return (
      <Link to={action.href} aria-label={action.label} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <button onClick={action.onClick} aria-label={action.label} className={className}>
      {content}
    </button>
  );
};

const MobileActionBar: React.FC = () => {
  const location = useLocation();
  const { onOpenCommunication, onOpenChat } = useSidebar();

  const scrollToFilters = () => {
    const filtersSection = document.getElementById('mobile-filters-section');
    if (filtersSection) {
      filtersSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      filtersSection.classList.add('animate-pulse');
      setTimeout(() => filtersSection.classList.remove('animate-pulse'), 1000);
    }
  };

  const scrollToSearch = () => {
    const searchSection = document.getElementById('mobile-search-section');
    const searchInput = searchSection?.querySelector('input');
    if (searchSection && searchInput) {
      searchSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => searchInput.focus(), 500);
    }
  };

  const goBack = () => {
    window.history.back();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageConfig = (): MobileActionBarConfig | null => {
    const path = location.pathname;

    if (path.startsWith('/places') || path.startsWith('/settings')) {
      return null;
    }

    // Lead details page - 5 slots
    if (path.startsWith('/leads/')) {
      return {
        leftAction: { icon: ArrowLeft, label: 'Back', onClick: goBack },
        leftSecondary: { icon: CheckSquare, label: 'Tasks', href: '/tasks' },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' },
        rightSecondary: { icon: MessageSquare, label: 'Chat', onClick: () => onOpenChat?.() },
        rightAction: { icon: Mail, label: 'Comms', onClick: () => onOpenCommunication?.('email') },
      };
    }

    // Leads list page
    if (path === '/app') {
      return {
        leftAction: { icon: Filter, label: 'Filters', onClick: scrollToFilters },
        rightAction: { icon: Search, label: 'Search', onClick: scrollToSearch },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' },
      };
    }

    // Tasks page
    if (path === '/tasks') {
      return {
        leftAction: { icon: Filter, label: 'Filters', onClick: scrollToFilters },
        rightAction: { icon: LayoutGrid, label: 'View', onClick: scrollToTop },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' },
      };
    }

    // Dashboard page
    if (path === '/dashboard') {
      return {
        leftAction: { icon: LayoutGrid, label: 'View', onClick: scrollToTop },
        rightAction: { icon: Search, label: 'Search', onClick: scrollToSearch },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' },
      };
    }

    // Companies page
    if (path === '/companies') {
      return {
        leftAction: { icon: Filter, label: 'Filter', onClick: scrollToFilters },
        rightAction: { icon: Search, label: 'Search', onClick: scrollToSearch },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' },
      };
    }

    return {
      centerAction: { icon: Plus, label: 'Add Lead', href: '/places' },
    };
  };

  const config = getPageConfig();
  if (!config) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
      <div className="backdrop-blur-lg bg-background/80 border-t border-border/40 shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent pointer-events-none" />
        
        <nav className="relative flex items-center justify-between h-16 max-w-screen-xl mx-auto px-4 safe-area-inset-bottom">
          {/* Left Action */}
          <ActionSlot action={config.leftAction} size="md" />

          {/* Left Secondary */}
          <ActionSlot action={config.leftSecondary} size="sm" />
          
          {/* Center FAB */}
          <div className="flex items-center justify-center">
            <div className="absolute h-16 w-16 rounded-full bg-primary/20 blur-xl animate-pulse-glow -mt-8" />
            <Link 
              to={config.centerAction.href}
              aria-label={config.centerAction.label}
              className="relative -mt-10 group"
            >
              <div className="relative h-14 w-14 rounded-full bg-gradient-to-br from-primary via-primary to-primary/80 shadow-2xl transform transition-all duration-300 hover:scale-110 active:scale-95 hover:shadow-primary/50 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/20 to-transparent opacity-50" />
                <config.centerAction.icon className="h-6 w-6 text-white relative z-10 transition-transform group-hover:rotate-90 duration-300" />
                <div className="absolute inset-0 rounded-full border-2 border-primary/30 scale-110 opacity-0 group-hover:scale-125 group-hover:opacity-100 transition-all duration-500" />
              </div>
            </Link>
          </div>

          {/* Right Secondary */}
          <ActionSlot action={config.rightSecondary} size="sm" />
          
          {/* Right Action */}
          <ActionSlot action={config.rightAction} size="md" />
        </nav>
      </div>
    </div>
  );
};

export default MobileActionBar;
